import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import {
  IconPackage, IconExternalLink, IconPlus, IconX, IconSearch, IconCheck,
  IconShieldCheck, IconAlertTriangle, IconShieldX,
  IconChevronRight, IconChevronDown, IconWand, IconBraces,
} from "@tabler/icons-react";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOptionalEditorContext } from "@/contexts/EditorContext";
import { useInsertions, insertTokenIntoEditor } from "@/hooks/useInsertions";

interface PackageEntry {
  name: string;
  options?: string;
}

interface PackagesModalProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
}

interface LatexMeta {
  latexDocumentClass?: string | null;
  latexDocumentClassOptions?: string | null;
  latexPackages?: string | null;
}

/** Catalog row from `/api/public/latex-coverage/packages`. */
interface CatalogPackage {
  slug: string;
  displayName: string;
  category: string;
  coverageLevel: "full" | "partial" | "shimmed" | "none" | "unsupported";
  notes: string | null;
  ctanUrl: string | null;
}

const COVERAGE_BADGE: Record<CatalogPackage["coverageLevel"], { label: string; classes: string; icon: typeof IconCheck }> = {
  full:        { label: "Full",        classes: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: IconShieldCheck },
  partial:     { label: "Partial",     classes: "bg-amber-50 text-amber-700 border-amber-200",       icon: IconAlertTriangle },
  shimmed:     { label: "Shimmed",     classes: "bg-blue-50 text-blue-700 border-blue-200",          icon: IconShieldCheck },
  none:        { label: "Detected",    classes: "bg-slate-50 text-slate-600 border-slate-200",       icon: IconAlertTriangle },
  unsupported: { label: "Unsupported", classes: "bg-rose-50 text-rose-700 border-rose-200",          icon: IconShieldX },
};

function parsePackages(json?: string | null): PackageEntry[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p.name === "string")
      .map((p: PackageEntry) => ({ name: p.name, options: p.options }));
  } catch {
    return [];
  }
}

function serialisePackages(list: PackageEntry[]): string {
  return JSON.stringify(
    list.map((p) => (p.options ? { name: p.name, options: p.options } : { name: p.name }))
  );
}

export function PackagesModal({ open, onClose, documentId }: PackagesModalProps) {
  const { fetchWithAuth } = useApiContext();
  const queryClient = useQueryClient();
  const [meta, setMeta] = useState<LatexMeta | null>(null);
  const [packages, setPackages] = useState<PackageEntry[]>([]);
  const [query, setQuery] = useState("");
  const [pendingOptions, setPendingOptions] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Catalog from the API — cached for 15 min server-side, react-query
  // keeps the in-memory copy across modal open/close cycles.
  const { data: catalog = [], isLoading: catalogLoading } = useQuery({
    queryKey: ["latex-package-catalog"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/public/latex-coverage/packages`);
      if (!res.ok) throw new Error(`Catalog fetch failed (${res.status})`);
      return (await res.json()) as CatalogPackage[];
    },
    staleTime: 15 * 60 * 1000,
    enabled: open,
  });

  const catalogBySlug = useMemo(() => {
    const m = new Map<string, CatalogPackage>();
    for (const p of catalog) m.set(p.slug, p);
    return m;
  }, [catalog]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setMeta(null);
    setQuery("");
    setPendingOptions("");
    setError(null);
    (async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/documents/${documentId}`, { method: "GET" });
        const doc = await handleResponse<LatexMeta>(res);
        if (active) {
          setMeta(doc);
          setPackages(parsePackages(doc.latexPackages));
        }
      } catch {
        if (active) setMeta({});
      }
    })();
    return () => { active = false; };
  }, [open, documentId, fetchWithAuth]);

  const installedNames = useMemo(() => new Set(packages.map((p) => p.name)), [packages]);
  const [showUnsupported, setShowUnsupported] = useState(false);

  // Per-package "what does this give me" inline expansion. We pull the
  // editor's insertions catalog (kernel + installed packages) and group
  // tokens by package_slug; clicking a row's chevron toggles its tokens
  // open. The same useInsertions hook backs the side panel and ⌘K, so a
  // token's behavior here is identical to the panel/palette.
  // Optional context — Studio mounts this modal too, where EditorProvider
  // doesn't exist. activeEditor is null in that case; the per-package
  // Insert buttons get disabled with their existing tooltip.
  const editorCtx = useOptionalEditorContext();
  const activeEditor = editorCtx?.activeEditor ?? null;
  const { data: insertions = [] } = useInsertions(documentId);
  const tokensByPackage = useMemo(() => {
    const map = new Map<string, typeof insertions>();
    for (const t of insertions) {
      const key = t.packageSlug ?? "(kernel)";
      const list = map.get(key) ?? [];
      list.push(t);
      map.set(key, list);
    }
    return map;
  }, [insertions]);
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());
  const toggleExpanded = (slug: string) => {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };
  const handleInsertAndClose = (token: typeof insertions[number]) => {
    if (!activeEditor) return;
    const ok = insertTokenIntoEditor(token, activeEditor, {
      source: "package-modal",
      documentId,
    });
    if (ok) onClose();
  };

  // Catalog filtered by query, excluding already-installed. Unsupported
  // packages are hidden by default — they break the parser, so showing
  // them as "installable" would be a bad first impression. The toggle
  // is visible when at least one unsupported entry would otherwise show.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? catalog.filter(
          (p) =>
            p.slug.toLowerCase().includes(q) ||
            p.displayName.toLowerCase().includes(q) ||
            (p.notes ?? "").toLowerCase().includes(q)
        )
      : catalog;
    return matches
      .filter((p) => !installedNames.has(p.slug))
      .filter((p) => showUnsupported || p.coverageLevel !== "unsupported")
      .sort((a, b) => {
        // Order: full > shimmed > partial > none > unsupported, then alpha
        const rank = { full: 0, shimmed: 1, partial: 2, none: 3, unsupported: 4 };
        const r = rank[a.coverageLevel] - rank[b.coverageLevel];
        return r !== 0 ? r : a.slug.localeCompare(b.slug);
      });
  }, [query, catalog, installedNames, showUnsupported]);

  const hiddenUnsupportedCount = useMemo(() => {
    if (showUnsupported) return 0;
    const q = query.trim().toLowerCase();
    return catalog
      .filter((p) => p.coverageLevel === "unsupported")
      .filter((p) => !installedNames.has(p.slug))
      .filter((p) => !q
        || p.slug.toLowerCase().includes(q)
        || p.displayName.toLowerCase().includes(q)
        || (p.notes ?? "").toLowerCase().includes(q))
      .length;
  }, [catalog, installedNames, query, showUnsupported]);

  const exactQuery = query.trim();
  const canAddCustom =
    exactQuery.length > 0 &&
    /^[a-zA-Z][a-zA-Z0-9_-]{0,40}$/.test(exactQuery) &&
    !installedNames.has(exactQuery) &&
    !catalogBySlug.has(exactQuery);

  const persist = async (next: PackageEntry[]) => {
    setIsSaving(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/documents/${documentId}`, {
        method: "PUT",
        body: JSON.stringify({ latexPackages: serialisePackages(next) }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setPackages(next);
      queryClient.invalidateQueries({ queryKey: ["document", documentId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = (name: string, options?: string) => {
    if (!name.trim()) return;
    if (installedNames.has(name)) return;
    const opts = (options ?? pendingOptions).trim();
    const next = [...packages, opts ? { name, options: opts } : { name }];
    setQuery("");
    setPendingOptions("");
    persist(next);
  };

  const handleRemove = (name: string) => {
    persist(packages.filter((p) => p.name !== name));
  };

  const documentClass = meta?.latexDocumentClass ?? null;
  const documentClassOptions = meta?.latexDocumentClassOptions ?? null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="packages-modal" className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconPackage size={18} /> LaTeX packages
          </DialogTitle>
          <DialogDescription>
            Browse the catalog, install packages into this document, or type a custom CTAN
            name. Coverage badges show how cleanly each package maps to Lilia's renderer.
          </DialogDescription>
        </DialogHeader>

        {/* Search / add row */}
        <div className="flex flex-col gap-2 pt-1">
          <div className="flex items-center gap-2 rounded-md border border-[var(--lilia-border)] bg-[var(--lilia-surface)] focus-within:ring-2 focus-within:ring-[var(--lilia-primary)]/30 px-3 py-1.5">
            <IconSearch size={14} className="text-[var(--lilia-text-muted)] shrink-0" />
            <input
              type="text"
              value={query}
              data-testid="packages-search"
              onChange={(e) => setQuery(e.target.value)}
              placeholder={catalogLoading ? "Loading catalog…" : "Search packages or type a custom name…"}
              disabled={catalogLoading}
              className="flex-1 bg-transparent border-0 outline-none text-sm text-[var(--lilia-text)] placeholder:text-[var(--lilia-text-muted)]"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (suggestions.length > 0 && !canAddCustom) {
                    handleAdd(suggestions[0].slug);
                  } else if (canAddCustom) {
                    handleAdd(exactQuery);
                  }
                }
              }}
            />
            <input
              type="text"
              value={pendingOptions}
              data-testid="packages-options"
              onChange={(e) => setPendingOptions(e.target.value)}
              placeholder="options (optional)"
              className="w-[180px] bg-transparent border-0 outline-none text-xs text-[var(--lilia-text-muted)] font-mono placeholder:text-[var(--lilia-text-muted)]/60"
            />
          </div>

          {canAddCustom && (
            <button
              data-testid="packages-add-custom"
              onClick={() => handleAdd(exactQuery)}
              className="self-start flex items-center gap-1.5 text-[12px] text-[var(--lilia-primary)] hover:underline"
              disabled={isSaving}
            >
              <IconPlus size={12} /> Add <code className="font-mono">{exactQuery}</code> as a custom (off-catalog) package
            </button>
          )}
        </div>

        <div className="overflow-y-auto flex-1 space-y-4 pr-1 mt-2">
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          {documentClass && (
            <section>
              <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)] mb-1.5">Document class</h3>
              <div className="rounded-md border border-[var(--lilia-border)] bg-[var(--lilia-bg-muted,var(--lilia-surface-alt))] px-3 py-2">
                <code className="font-mono text-xs">
                  {`\\documentclass`}
                  {documentClassOptions ? `[${documentClassOptions}]` : ""}
                  {`{${documentClass}}`}
                </code>
              </div>
            </section>
          )}

          {/* Currently installed */}
          {packages.length > 0 && (
            <section>
              <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)] mb-1.5">
                Installed in this document ({packages.length})
              </h3>
              <ul className="space-y-1">
                {packages.map((p) => {
                  const meta = catalogBySlug.get(p.name);
                  const badge = meta ? COVERAGE_BADGE[meta.coverageLevel] : null;
                  const BadgeIcon = badge?.icon;
                  const tokens = tokensByPackage.get(p.name) ?? [];
                  const expanded = expandedSlugs.has(p.name);
                  const canExpand = tokens.length > 0;
                  return (
                    <li
                      key={p.name}
                      data-testid="packages-installed"
                      data-name={p.name}
                      className="rounded-md border border-[var(--lilia-border)] group"
                    >
                      <div className="flex items-start gap-3 px-3 py-2">
                        <button
                          onClick={() => canExpand && toggleExpanded(p.name)}
                          disabled={!canExpand}
                          title={canExpand
                            ? (expanded ? "Hide insertions" : `Show ${tokens.length} insertion${tokens.length === 1 ? "" : "s"}`)
                            : "No insertable commands available"}
                          className="shrink-0 mt-0.5 text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)] disabled:opacity-30 disabled:cursor-default"
                        >
                          {canExpand ? (
                            expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
                          ) : (
                            <IconCheck size={14} className="text-[var(--lilia-success)]" />
                          )}
                        </button>
                        <span className="font-mono text-[11px] bg-[var(--lilia-primary-subtle,var(--lilia-primary-light))] text-[var(--lilia-primary)] px-1.5 py-0.5 rounded shrink-0">
                          {p.name}
                        </span>
                        {badge && BadgeIcon && (
                          <span
                            className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${badge.classes}`}
                            title={meta?.notes ?? badge.label}
                          >
                            <BadgeIcon size={10} /> {badge.label}
                          </span>
                        )}
                        <span className="flex-1 text-[12px] text-[var(--lilia-text-secondary)]">
                          {meta?.displayName ?? <span className="italic text-[var(--lilia-text-muted)]">Custom — not in the catalog</span>}
                          {canExpand && (
                            <span className="text-[10px] text-[var(--lilia-text-muted)] ml-1">
                              · {tokens.length} insertion{tokens.length === 1 ? "" : "s"}
                            </span>
                          )}
                          {p.options && (
                            <span className="block text-[11px] text-[var(--lilia-text-muted)] font-mono mt-0.5">
                              options: {p.options}
                            </span>
                          )}
                        </span>
                        {meta?.ctanUrl && (
                          <a
                            href={meta.ctanUrl}
                            target="_blank" rel="noopener noreferrer"
                            className="shrink-0 text-[var(--lilia-text-muted)] hover:text-[var(--lilia-primary)]"
                            title="View on CTAN"
                          >
                            <IconExternalLink size={12} />
                          </a>
                        )}
                        <button
                          data-testid="packages-remove"
                          onClick={() => handleRemove(p.name)}
                          disabled={isSaving}
                          title={`Remove ${p.name}`}
                          className="shrink-0 p-1 rounded text-[var(--lilia-text-muted)] hover:bg-red-50 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <IconX size={13} />
                        </button>
                      </div>

                      {/* Inline insertions list — appears when chevron expanded.
                          Click any token row to insert it at the editor cursor
                          and close the modal. */}
                      {expanded && canExpand && (
                        <ul className="border-t border-[var(--lilia-border)] bg-[var(--lilia-bg-muted,var(--lilia-surface-alt))]/50">
                          {tokens.map((t) => {
                            const isEnv = t.kind === "environment" || t.expectsBody;
                            return (
                              <li key={`${t.kind}/${t.name}`}>
                                <button
                                  onClick={() => handleInsertAndClose(t)}
                                  disabled={!activeEditor}
                                  title={!activeEditor
                                    ? "Click into the editor first"
                                    : (t.notes ?? `Insert \\${t.name}`)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-[var(--lilia-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-t border-[var(--lilia-border-light,var(--lilia-border))]/50 first:border-t-0"
                                >
                                  {isEnv ? (
                                    <IconBraces size={12} className="text-[var(--lilia-text-muted)] shrink-0" />
                                  ) : (
                                    <IconWand size={12} className="text-[var(--lilia-text-muted)] shrink-0" />
                                  )}
                                  <code className="font-mono text-[11px] text-[var(--lilia-text)] shrink-0">
                                    {isEnv ? t.name : `\\${t.name}`}
                                  </code>
                                  {t.notes && (
                                    <span className="flex-1 truncate text-[11px] text-[var(--lilia-text-muted)]">
                                      {t.notes}
                                    </span>
                                  )}
                                  <span className="shrink-0 text-[10px] text-[var(--lilia-primary)] opacity-0 group-hover:opacity-60 hover:opacity-100">
                                    Insert →
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Catalog explorer */}
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">
                {query.trim()
                  ? `Suggestions (${suggestions.length})`
                  : `Catalog (${suggestions.length})`}
              </h3>
              {hiddenUnsupportedCount > 0 && (
                <button
                  onClick={() => setShowUnsupported(true)}
                  className="text-[10px] text-[var(--lilia-text-muted)] hover:text-[var(--lilia-primary)] underline-offset-2 hover:underline"
                >
                  Show {hiddenUnsupportedCount} unsupported
                </button>
              )}
              {showUnsupported && (
                <button
                  onClick={() => setShowUnsupported(false)}
                  className="text-[10px] text-[var(--lilia-text-muted)] hover:text-[var(--lilia-primary)] underline-offset-2 hover:underline"
                >
                  Hide unsupported
                </button>
              )}
            </div>
            {catalogLoading ? (
              <div className="text-sm text-[var(--lilia-text-muted)] py-6 text-center italic">
                Loading catalog…
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-sm text-[var(--lilia-text-muted)] py-6 text-center italic">
                {query.trim()
                  ? "No catalog matches. Type a CTAN name + Enter to add a custom package."
                  : "All catalog packages already installed."}
              </div>
            ) : (
              <ul className="space-y-1">
                {suggestions.slice(0, query.trim() ? 30 : 50).map((p) => {
                  const badge = COVERAGE_BADGE[p.coverageLevel];
                  const BadgeIcon = badge.icon;
                  return (
                    <li key={p.slug}>
                      <button
                        data-testid="packages-suggestion"
                        data-name={p.slug}
                        onClick={() => handleAdd(p.slug)}
                        disabled={isSaving}
                        className="w-full flex items-start gap-3 text-left rounded-md border border-transparent hover:border-[var(--lilia-border)] hover:bg-[var(--lilia-hover)] px-3 py-2 transition-colors"
                      >
                        <span className="font-mono text-[11px] bg-[var(--lilia-bg-muted,var(--lilia-surface-alt))] text-[var(--lilia-text)] px-1.5 py-0.5 rounded shrink-0">
                          {p.slug}
                        </span>
                        <span
                          className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded ${badge.classes}`}
                          title={p.notes ?? badge.label}
                        >
                          <BadgeIcon size={10} /> {badge.label}
                        </span>
                        <span className="shrink-0 text-[10px] text-[var(--lilia-text-muted)] uppercase tracking-wider">
                          {p.category}
                        </span>
                        <span className="flex-1 text-[12px] text-[var(--lilia-text-secondary)]">
                          {p.displayName}
                        </span>
                        <IconPlus size={14} className="text-[var(--lilia-text-muted)] shrink-0 mt-0.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-[var(--lilia-border)]">
          <span className="text-[11px] text-[var(--lilia-text-muted)]">
            {isSaving ? "Saving…" : `${packages.length} installed · ${catalog.length} in catalog`}
          </span>
          <Button variant="outline" size="sm" data-testid="packages-close" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
